import { Check, ChevronUp, Pause, Play, RotateCcw, Scissors, Sparkles, Undo2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type Meeting,
  type MeetingProposal,
  type TranscriptSegment,
  type TranscriptSpeaker,
} from "@/lib/api";
import { VerdictToggle } from "@/components/VerdictToggle";
import {
  adoptedGroups,
  clock,
  decibels,
  mergeUp,
  partDescription,
  partRanges,
  partReason,
  partStretch,
  partTitle,
  partVoices,
  patchPart,
  peopleIn,
  speakerLookup,
  stretchesInside,
  unmerge,
  useMeetingParts,
} from "@/lib/meetingParts";
import { cn } from "@/lib/cn";
import { formatClockAt, formatDuration } from "@/lib/format";
import { useAudioFragment } from "@/lib/useAudioFragment";

/** How much of a proposed stretch's start/end the "Start"/"End" listen buttons play — long
 * enough to actually judge whether the cut lands in the right place, not just recognise a
 * voice (that's MAX_SAMPLE_MS's job, on the Speakers tab). */
const BOUNDARY_SAMPLE_MS = 60_000;

/**
 * SPCH-13 — the table of meetings the audio phase proposes for a long recording, edited row
 * by row: approve or discard each part, merge adjacent ones into a single meeting, then
 * start every approved meeting in the one click that also adopts them.
 *
 * Every name in it is resolved here, from the speakers step 1 of this wizard is naming, so
 * confirming who a voice is renames a row's default title and reason without a request —
 * until the reader edits that row's title by hand, at which point it is theirs to keep.
 *
 * SPCH-14 — a row is also a way into review: "Review & split" opens the whole recording in
 * review mode at that part, where its divider can be moved a line at a time and a new part cut
 * at any line. The table and review edit one draft (`useMeetingParts`), kept in this browser, so
 * what was cut there is a row here on the way back — and adopting is still this table's one,
 * all-or-nothing click.
 */
export function ProposedMeetings({
  recordingId,
  proposal,
  speakers,
  segments,
  meetings,
  onApplied,
  player,
  startedAt,
}: {
  recordingId: string;
  proposal: MeetingProposal;
  speakers: TranscriptSpeaker[];
  segments: TranscriptSegment[];
  meetings: Meeting[] | null;
  onApplied: (meetings: Meeting[]) => void;
  player: ReturnType<typeof useAudioFragment>;
  startedAt: string | null;
}) {
  const { api } = useApp();
  const [applying, setApplying] = useState(false);
  const [confirmingReplace, setConfirmingReplace] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const stretches = proposal.status === "ready" ? proposal.stretches : null;
  const { parts, update } = useMeetingParts(recordingId, stretches);
  const nameOf = useMemo(() => speakerLookup(speakers), [speakers]);

  const shell =
    "rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-5)]";

  if (proposal.status === "pending") {
    return (
      <section className={shell} aria-label="Proposed meetings">
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Proposing meetings…</p>
      </section>
    );
  }

  if (proposal.status === "failed") {
    return (
      <section className={shell} aria-label="Proposed meetings">
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          The meetings in this recording could not be proposed. Split it by hand below, or restart the pipeline at
          propose_meetings.
        </p>
      </section>
    );
  }

  if (!stretches || stretches.length === 0 || !parts) return null;

  // The time of day a stretch fell at, when the recording's own start is known — falling
  // back to its position within the recording for one filed before that was carried.
  const clockOf = (ms: number) => formatClockAt(startedAt, ms) ?? clock(ms);

  const started = meetings?.some((meeting) => meeting.status !== null) ?? false;
  const busy = meetings?.some((meeting) => meeting.status === "queued" || meeting.status === "processing") ?? false;
  const canApply = proposal.canApply && !started;
  // Meetings were processed already — by mistake as one whole-recording meeting, or from an
  // earlier version of this table. Adopting again replaces them, which is said before it is done.
  const canReplace = (proposal.canReplace ?? false) && started && !busy;
  const awaiting = proposal.awaitingAdoption ?? false;
  const unnamed = new Set(
    stretches.flatMap((stretch) => stretch.voices.map((voice) => voice.speaker)).filter((label) => !nameOf(label).confirmed),
  );
  const approvedCount = parts.filter((part) => part.verdict === "meeting").length;
  const ranges = partRanges(parts, stretches);
  const reviewAt = (position: number) => `/recordings/${recordingId}/review?parts=${position + 1}`;

  // Approving creates the meetings and starts nothing: each is then processed from the list
  // below, one at a time or all at once, so nothing is processed before it was approved.
  async function approve(replaceStarted: boolean) {
    if (!parts || !stretches) return;
    setApplying(true);
    setProblem(null);
    try {
      onApplied(await api.applyMeetingProposal(recordingId, adoptedGroups(parts, stretches, nameOf), replaceStarted));
      setConfirmingReplace(false);
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The proposal could not be used.");
    } finally {
      setApplying(false);
    }
  }

  const meetingsLabel = `${approvedCount} ${approvedCount === 1 ? "meeting" : "meetings"}`;

  return (
    <section className={shell} aria-label="Proposed meetings">
      <div className="flex flex-wrap items-start justify-between gap-[var(--space-3)]">
        <div className="min-w-0">
          <h3 className="flex items-center gap-[var(--space-2)] text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">
            <Sparkles size={16} strokeWidth={2} className="shrink-0 text-[var(--primary)]" aria-hidden />
            Proposed meetings
          </h3>
          <p className="mt-1 max-w-[70ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {stretches.length} {stretches.length === 1 ? "stretch" : "stretches"} found by how loud the room is and
            who is talking{proposal.model ? ", and named from what was said." : "."} Approve, discard or merge a row,
            review one to split it or move where it starts, then create the meetings you keep. Nothing is processed
            until you start it.
            {unnamed.size > 0 && " Voices not yet named keep a number in the titles."}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-[var(--space-2)]">
          {!applying && (
            <Link
              to={`/recordings/${recordingId}/review?parts`}
              className="flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
            >
              <Scissors size={13} strokeWidth={2} />
              Review all parts
            </Link>
          )}

          {canApply && (
            <button
              type="button"
              disabled={applying || approvedCount === 0}
              onClick={() => void approve(false)}
              className={
                awaiting
                  ? "flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  : "flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              <Check size={13} strokeWidth={2} />
              {applying ? "Creating…" : awaiting ? `Create ${meetingsLabel}` : `Recreate as ${meetingsLabel}`}
            </button>
          )}

          {canReplace && !confirmingReplace && (
            <button
              type="button"
              disabled={applying || approvedCount === 0}
              onClick={() => setConfirmingReplace(true)}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw size={13} strokeWidth={2} />
              Replace with {meetingsLabel}
            </button>
          )}
        </div>
      </div>

      {awaiting && canApply && approvedCount > 0 && (
        <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Creating the meetings processes nothing yet. Each one is started from the list below, on its own or all at
          once.
        </p>
      )}

      {canApply && approvedCount === 0 && (
        <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Every row is set to discard, so there is nothing to create yet — approve at least one.
        </p>
      )}

      {started && busy && (
        <p className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-muted)]">
          A meeting below is being processed. Cancel it there before changing how this recording is split.
        </p>
      )}

      {started && !busy && !canReplace && (
        <p className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-muted)]">
          A meeting below has already been processed, so this proposal can no longer replace them.
        </p>
      )}

      {canReplace && !confirmingReplace && (
        <p className="mt-[var(--space-3)] max-w-[70ch] text-[length:var(--text-sm)] text-[var(--text-muted)]">
          The meetings below were already processed. To split this recording differently, set the rows here and
          replace them.
        </p>
      )}

      {canReplace && confirmingReplace && (
        <div
          role="alertdialog"
          aria-label="Replace the processed meetings"
          className="mt-[var(--space-3)] flex flex-wrap items-center justify-between gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--warning)] px-[var(--space-4)] py-[var(--space-3)]"
        >
          <p className="max-w-[70ch] text-[length:var(--text-sm)] text-[var(--text-primary)]">
            Replace the processed {meetings?.length === 1 ? "meeting" : "meetings"} with {meetingsLabel}? Their
            summaries, and every insight and entity tag found only there, are discarded. Transcript corrections are
            kept. Nothing new is processed until you start it.
          </p>
          <div className="flex shrink-0 items-center gap-[var(--space-2)]">
            <button
              type="button"
              disabled={applying}
              onClick={() => setConfirmingReplace(false)}
              className="h-8 px-3 text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={applying}
              onClick={() => void approve(true)}
              className="flex h-8 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
            >
              {applying ? "Replacing…" : "Replace and discard"}
            </button>
          </div>
        </div>
      )}

      {problem && (
        <p role="alert" className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <div className="mt-[var(--space-4)] overflow-x-auto">
        <table className="w-full min-w-[840px] border-collapse text-left text-[length:var(--text-sm)]">
          <thead>
            <tr className="border-b border-[var(--border-subtle)] text-[length:var(--text-xs)] uppercase tracking-wide text-[var(--text-muted)]">
              <th scope="col" className="py-3 pr-[var(--space-6)] pl-[var(--space-2)] font-semibold">Time</th>
              <th scope="col" className="py-3 pr-[var(--space-6)] font-semibold">What</th>
              <th scope="col" className="py-3 pr-[var(--space-6)] font-semibold">Who</th>
              <th scope="col" className="py-3 pr-[var(--space-2)] font-semibold">Why here</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((part, position) => {
              const { startMs, endMs } = ranges[position]!;
              const people = peopleIn(partVoices(parts, position, stretches, segments, speakers), nameOf);
              const title = partTitle(part, stretches, nameOf);
              const description = partDescription(part, stretches, nameOf);
              const inside = stretchesInside(parts, position, stretches);
              const measured = partStretch(part, stretches);
              const discarded = part.verdict === "discard";

              return (
                <tr
                  key={startMs}
                  className={cn(
                    "border-b border-[var(--border-subtle)] align-top last:border-b-0 transition-opacity",
                    discarded && "opacity-60",
                  )}
                >
                  <td className="whitespace-nowrap py-4 pr-[var(--space-6)] pl-[var(--space-2)] tabular-nums text-[var(--text-secondary)]">
                    {clockOf(startMs)}–{clockOf(endMs)}
                    <div className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
                      {formatDuration(endMs - startMs)}
                    </div>
                    {position > 0 && !applying && (
                      <button
                        type="button"
                        onClick={() => update((current) => mergeUp(current, position))}
                        className="mt-2 flex h-6 items-center gap-1 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-2 text-[length:var(--text-2xs)] font-medium text-[var(--vl-bridge-soft)] transition hover:brightness-125"
                      >
                        <ChevronUp size={11} strokeWidth={2.5} />
                        Merge up
                      </button>
                    )}
                    {/* SPCH-13 — the machine's cut is worth checking against the sound
                        itself. Full playback would be the whole point of "listen to it",
                        but a minute at each edge is what actually tells a reader whether
                        the boundary lands where they'd have cut it themselves. */}
                    <div className="mt-2 flex flex-wrap items-center gap-[var(--space-2)]">
                      <ListenButton
                        player={player}
                        id={`${startMs}-start`}
                        startMs={startMs}
                        endMs={endMs}
                        maxMs={BOUNDARY_SAMPLE_MS}
                        label={`Play the start of this stretch, from ${clockOf(startMs)}`}
                      >
                        Start
                      </ListenButton>
                      <ListenButton
                        player={player}
                        id={`${startMs}-end`}
                        startMs={Math.max(startMs, endMs - BOUNDARY_SAMPLE_MS)}
                        endMs={endMs}
                        maxMs={BOUNDARY_SAMPLE_MS}
                        label={`Play the end of this stretch, up to ${clockOf(endMs)}`}
                      >
                        End
                      </ListenButton>
                    </div>
                  </td>
                  <td className="py-4 pr-[var(--space-6)]">
                    <div className="flex flex-wrap items-center gap-[var(--space-2)]">
                      <VerdictToggle
                        verdict={part.verdict}
                        disabled={applying}
                        onChange={(verdict) => update((current) => patchPart(current, position, { verdict }))}
                      />
                      {inside > 1 && (
                        <button
                          type="button"
                          disabled={applying}
                          onClick={() => update((current) => unmerge(current, position, stretches))}
                          className="flex items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50"
                        >
                          <Undo2 size={11} strokeWidth={2.5} />
                          Unmerge
                        </button>
                      )}
                      {!applying && (
                        <Link
                          to={reviewAt(position)}
                          aria-label={`Review and split ${title || "this part"} from ${clockOf(startMs)}`}
                          className="flex items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          <Scissors size={11} strokeWidth={2.5} />
                          Review & split
                        </Link>
                      )}
                    </div>
                    <input
                      type="text"
                      value={title}
                      disabled={applying}
                      onChange={(event) => update((current) => patchPart(current, position, { titleOverride: event.target.value }))}
                      placeholder="Untitled"
                      className="mt-2 h-8 w-full max-w-[36ch] rounded-[var(--radius-md)] border border-transparent bg-transparent px-1 text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:border-[var(--border-default)] focus:border-[var(--border-default)] focus:bg-[var(--surface-1)] focus:outline-none"
                    />
                    <textarea
                      value={description}
                      disabled={applying}
                      onChange={(event) =>
                        update((current) => patchPart(current, position, { descriptionOverride: event.target.value }))
                      }
                      placeholder="Description (optional)"
                      rows={2}
                      className="mt-1 w-full max-w-[42ch] resize-none rounded-[var(--radius-md)] border border-transparent bg-transparent px-1 text-[var(--text-secondary)] transition hover:border-[var(--border-default)] focus:border-[var(--border-default)] focus:bg-[var(--surface-1)] focus:outline-none"
                    />
                    {inside > 1 && (
                      <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
                        {inside} stretches merged into one meeting.
                      </p>
                    )}
                  </td>
                  <td className="py-4 pr-[var(--space-6)]">
                    {people.length === 0 ? (
                      <span className="text-[var(--text-muted)]">Nobody talks</span>
                    ) : (
                      <ul className="flex flex-col gap-0.5">
                        {people.map((person) => (
                          <li key={person.name} className="flex items-center gap-[var(--space-2)] whitespace-nowrap">
                            <span
                              className="h-2 w-2 shrink-0 rounded-[var(--radius-full)]"
                              style={{ background: person.colour }}
                              aria-hidden
                            />
                            <span className="text-[var(--text-primary)]">{person.name}</span>
                            <span className="tabular-nums text-[var(--text-muted)]">{Math.round(person.share * 100)}%</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-4 pr-[var(--space-2)] text-[var(--text-secondary)]">
                    {partReason(parts, position, stretches, nameOf)}
                    {measured && (
                      <div className="mt-1 text-[length:var(--text-xs)] tabular-nums text-[var(--text-muted)]">
                        {Math.round(measured.talkSecondsPerMinute)} s talk/min
                        {measured.floorDb !== null && ` · floor ${decibels(measured.floorDb)} dBFS`}
                        {measured.rangeDb !== null && ` · range ${Math.round(measured.rangeDb)} dB`}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {player.problem && (
        <p role="alert" className="mt-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--warning)]">
          {player.problem}
        </p>
      )}
    </section>
  );
}

/**
 * A small pill next to a stretch's time range: play a bounded clip from the given point
 * (optionally capped shorter via `maxMs`) — enough to hear whether a cut actually lands
 * where it sounds like it should, not a full replay.
 */
function ListenButton({
  player,
  id,
  startMs,
  endMs,
  maxMs,
  label,
  children,
}: {
  player: ReturnType<typeof useAudioFragment>;
  id: string;
  startMs: number;
  endMs: number;
  maxMs?: number;
  label: string;
  children: ReactNode;
}) {
  const playing = player.playing === id;

  return (
    <button
      type="button"
      onClick={() => player.play({ id, startMs, endMs }, maxMs)}
      aria-label={playing ? "Stop" : label}
      className="flex h-6 shrink-0 items-center gap-1 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-2 text-[length:var(--text-2xs)] font-medium text-[var(--vl-bridge-soft)] transition hover:brightness-125"
    >
      {playing ? <Pause size={10} strokeWidth={2.5} /> : <Play size={10} strokeWidth={2.5} />}
      {children}
    </button>
  );
}
